import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';

// ── Config ────────────────────────────────────────────────────────────────────
const PILOMENT_URL = process.env.REACT_APP_PILOMENT_BACKEND_URL || 'https://paymentgateway-zes0.onrender.com';

// ── Currency config (mirrors Services.jsx) ────────────────────────────────────
const CURRENCY_CONFIG = {
  USD: { symbol: '$',    name: 'US Dollar' },
  INR: { symbol: '₹',   name: 'Indian Rupee' },
  EUR: { symbol: '€',   name: 'Euro' },
  GBP: { symbol: '£',   name: 'British Pound' },
  AUD: { symbol: 'A$',  name: 'Australian Dollar' },
  CAD: { symbol: 'C$',  name: 'Canadian Dollar' },
  SGD: { symbol: 'S$',  name: 'Singapore Dollar' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham' },
};

// ── Animated loader ───────────────────────────────────────────────────────────
const Loader = ({ label }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
    <div style={{
      width: 48, height: 48,
      border: '3px solid rgba(124,58,237,0.15)',
      borderTop: '3px solid #7c3aed',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    {label && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0 }}>{label}</p>}
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ── Step indicator ────────────────────────────────────────────────────────────
const Steps = ({ current }) => {
  const steps = ['Cart ready', 'Payment', 'Activating', 'Done'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 40 }}>
      {steps.map((s, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: done ? '#7c3aed' : active ? 'rgba(124,58,237,0.3)' : 'rgba(255,255,255,0.05)',
                border: active ? '2px solid #7c3aed' : done ? '2px solid #7c3aed' : '2px solid rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: done || active ? '#fff' : 'rgba(255,255,255,0.3)',
                transition: 'all 0.3s'
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 10, color: active ? '#a78bfa' : done ? '#7c3aed' : 'rgba(255,255,255,0.25)', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                {s}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: i < current ? '#7c3aed' : 'rgba(255,255,255,0.07)', margin: '-14px 8px 0', transition: 'background 0.5s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function PaymentPage() {
  const { cartId: cart_id } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase]       = useState('loading');
  const [step, setStep]         = useState(0);
  const [cartInfo, setCartInfo] = useState(null);
  const [error, setError]       = useState('');
  const [txnId, setTxnId]       = useState('');
  const [email, setEmail]       = useState('');
  const [phone, setPhone]       = useState('');

  // ── Currency state ─────────────────────────────────────────────────────────
  const [currency, setCurrency]       = useState('USD');
  const [liveRate, setLiveRate]       = useState(1);      // USD → user currency
  const [rateLoaded, setRateLoaded]   = useState(false);

  const rzpScriptLoaded = useRef(false);
  const token = localStorage.getItem('access_token');

  // ── Load Razorpay script once ─────────────────────────────────────────────
  useEffect(() => {
    if (rzpScriptLoaded.current) return;
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => { rzpScriptLoaded.current = true; };
    document.body.appendChild(s);
  }, []);

  // ── Detect currency from localStorage (set by Services.jsx) ───────────────
  useEffect(() => {
    let detectedCurrency = 'USD';
    try {
      const saved = localStorage.getItem('userLocation');
      if (saved) {
        const loc = JSON.parse(saved);
        if (loc.details?.countryCode) {
          const COUNTRY_TO_CURRENCY = {
            US: 'USD', IN: 'INR', GB: 'GBP', AU: 'AUD', CA: 'CAD',
            SG: 'SGD', AE: 'AED', FR: 'EUR', DE: 'EUR', IT: 'EUR',
            ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', IE: 'EUR',
            PT: 'EUR', GR: 'EUR', FI: 'EUR', LU: 'EUR',
          };
          detectedCurrency = COUNTRY_TO_CURRENCY[loc.details.countryCode] || 'USD';
        }
      }
    } catch (e) {}

    setCurrency(detectedCurrency);

    // Fetch live rate from open.er-api.com (free, CORS-friendly)
    if (detectedCurrency === 'USD') {
      setLiveRate(1);
      setRateLoaded(true);
      return;
    }

    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(data => {
        const rate = data.rates?.[detectedCurrency];
        if (rate) setLiveRate(rate);
        else setLiveRate(1);
      })
      .catch(() => setLiveRate(1))
      .finally(() => setRateLoaded(true));
  }, []);

  // ── Format price in user's currency ──────────────────────────────────────
  const fmt = (usdAmount) => {
    const converted = usdAmount * liveRate;
    const symbol = CURRENCY_CONFIG[currency]?.symbol || '$';
    if (currency === 'INR') return `${symbol}${Math.round(converted).toLocaleString('en-IN')}`;
    return `${symbol}${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Fetch cart total from PB backend ──────────────────────────────────────
  useEffect(() => {
    if (!cart_id) { setError('No cart found.'); setPhase('failure'); return; }
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/cart/${cart_id}/total`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Cart not found');
        const data = await res.json();
        setCartInfo(data);

        const uRes = await fetch(`${baseUrl}/getUserIDByToken`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (uRes.ok) {
          const u = await uRes.json();
          setEmail(u.email || '');
          setPhone(u.phone_number || '');
        }
        setPhase('ready');
        setStep(0);
      } catch (e) {
        setError(e.message || 'Could not load cart.');
        setPhase('failure');
      }
    })();
  }, [cart_id]);

  // ── Core payment function ─────────────────────────────────────────────────
  const startPayment = async () => {
    if (!rzpScriptLoaded.current || !window.Razorpay) {
      setError('Payment SDK not loaded. Please refresh and try again.');
      return;
    }

    setPhase('processing');
    setStep(1);

    try {
      // ── Step A: Get user ID ───────────────────────────────────────────────
      const uRes = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!uRes.ok) throw new Error('Session expired. Please log in again.');
      const { user_id } = await uRes.json();

      // ── Step B: Get fresh live rate and create order ──────────────────────
      let currentRate = liveRate;
      let currentCurrency = currency;

      // Always fetch a fresh rate at payment time for accuracy
      if (currentCurrency !== 'USD') {
        try {
          const fxRes = await fetch(`https://open.er-api.com/v6/latest/USD`);
          const fxData = await fxRes.json();
          currentRate = fxData.rates?.[currentCurrency] || currentRate;
        } catch (e) {
          // Use cached rate if fetch fails
        }
      }

      // Convert USD total to user's currency (smallest unit for payment)
      const amountInCurrency = Math.round(cartInfo.final_total * currentRate * 100); // in paise/cents

      const orderRes = await fetch(`${PILOMENT_URL}/api/v1/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cart_id,
          amount: cartInfo.final_total * currentRate, // in currency units (Piloment converts to paise)
          currency: currentCurrency,
          user_id,
          ...(cartInfo?.plan ? { payment_type: "subscription", plan_code: cartInfo.plan } : {})
        })
      });

      if (!orderRes.ok) {
        const e = await orderRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not create payment order');
      }
      const orderData = await orderRes.json();

      // ── Step C: Open Razorpay popup ───────────────────────────────────────
      await new Promise((resolve, reject) => {
        const options = {
          key:         orderData.key || process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount:      orderData.amount,
          currency:    orderData.currency || currentCurrency,
          ...(orderData.gateway_subscription_id
            ? { subscription_id: orderData.gateway_subscription_id }
            : { order_id: orderData.gateway_order_id }),
          name:        'ProducerBuddy',
          description: cartInfo?.plan ? `${cartInfo.plan} Plan Subscription` : 'Tool Subscription',
          image:       '/logo.png',
          prefill:     { email, contact: phone },
          theme:       { color: '#7c3aed' },

          handler: async (response) => {
            try {
              setStep(2);

              // ── Step D: Verify with Piloment ─────────────────────────────
              const verifyRes = await fetch(`${PILOMENT_URL}/api/v1/payments/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id:   response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature:  response.razorpay_signature,
                  cart_id
                })
              });
              if (!verifyRes.ok) throw new Error('Payment verification failed');

              // ── Step E: Activate on PB backend ────────────────────────────
              const activateRes = await fetch(`${baseUrl}/payment/activate`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  cart_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id:   response.razorpay_order_id,
                  amount:              cartInfo.final_total * currentRate,
                  status:              'success'
                })
              });
              if (!activateRes.ok) {
                const e = await activateRes.json().catch(() => ({}));
                throw new Error(e.detail || 'Tool activation failed. Contact support with your payment ID.');
              }

              setTxnId(response.razorpay_payment_id);
              setStep(3);
              setPhase('success');
              resolve();
            } catch (err) {
              reject(err);
            }
          },

          modal: {
            ondismiss: () => {
              setPhase('ready');
              setStep(0);
              resolve();
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          reject(new Error(resp.error?.description || 'Payment failed'));
        });
        rzp.open();
      });

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setPhase('failure');
      setStep(0);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <SEO
        title="Choose Your Plan"
        description="Select the ProducerBuddy plan that fits your film production needs and unlock the full suite of AI-powered tools."
        url="https://producerbuddy.pilomation.com/plans"
        robots="noindex, nofollow"
      />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');
        body { font-family: 'DM Sans', sans-serif; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        .pay-btn { transition: all 0.2s cubic-bezier(.22,1,.36,1); }
        .pay-btn:hover:not(:disabled) { transform: scale(1.025); box-shadow: 0 0 28px rgba(124,58,237,0.45); }
        .pay-btn:active:not(:disabled) { transform: scale(0.98); }
      `}</style>

      <Nav />

      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at 20% 0%, #120a2e 0%, #0a0a12 55%)',
        paddingTop: 80, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '100px 24px 60px'
      }}>
        <div style={{ position: 'fixed', top: -100, left: '50%', transform: 'translateX(-50%)', width: 500, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(124,58,237,0.12) 0%,transparent 70%)', pointerEvents: 'none' }} />

        <div style={{
          width: '100%', maxWidth: 520,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20, padding: '36px 36px',
          animation: 'fadeUp 0.5s cubic-bezier(.22,1,.36,1) both'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(135deg,#7c3aed,#0369a1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>⚡</div>
            <div>
              <p style={{ fontSize: 16, fontWeight: 500, color: '#fff', margin: 0 }}>ProducerBuddy</p>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: 0, letterSpacing: '0.08em' }}>SECURE CHECKOUT</p>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
              SSL Secured
            </div>
          </div>

          {phase !== 'loading' && <Steps current={step} />}

          {/* LOADING */}
          {phase === 'loading' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Loader label="Loading your order..." />
            </div>
          )}

          {/* READY */}
          {phase === 'ready' && cartInfo && (
            <div style={{ animation: 'fadeUp 0.4s ease both' }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 18, marginBottom: 20 }}>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>Order summary</p>

                {cartInfo.plan_cost > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Plan ({cartInfo.plan || 'Selected'})</span>
                    <span style={{ fontSize: 13, color: '#fff', fontFamily: "'DM Mono',monospace" }}>{fmt(cartInfo.plan_cost)}</span>
                  </div>
                )}
                {cartInfo.tier_cost > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Tier ({cartInfo.tier || 'Selected'})</span>
                    <span style={{ fontSize: 13, color: '#fff', fontFamily: "'DM Mono',monospace" }}>{fmt(cartInfo.tier_cost)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Tools</span>
                  <span style={{ fontSize: 13, color: '#fff', fontFamily: "'DM Mono',monospace" }}>{fmt(cartInfo.tools_cost)}</span>
                </div>

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>
                    Total {cartInfo.billing_cycle === 'yearly' ? '(yearly, 20% off)' : '(monthly)'}
                  </span>
                  <span style={{ fontSize: 22, fontWeight: 500, color: '#a78bfa', fontFamily: "'DM Mono',monospace" }}>
                    {rateLoaded ? fmt(cartInfo.final_total) : '...'}
                  </span>
                </div>


              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
                {['UPI', 'Credit Card', 'Debit Card', 'Net Banking', 'Wallets'].map(m => (
                  <span key={m} style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, padding: '3px 8px' }}>{m}</span>
                ))}
              </div>

              <button
                onClick={startPayment}
                disabled={!rateLoaded}
                className="pay-btn"
                style={{
                  width: '100%', padding: '15px',
                  background: rateLoaded ? 'linear-gradient(135deg,#7c3aed,#0369a1)' : 'rgba(255,255,255,0.1)',
                  color: '#fff', border: 'none', borderRadius: 12,
                  fontSize: 15, fontWeight: 500, cursor: rateLoaded ? 'pointer' : 'not-allowed',
                  fontFamily: "'DM Sans',sans-serif"
                }}
              >
                {rateLoaded ? `Pay ${fmt(cartInfo.final_total)} securely →` : 'Loading rates...'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.2)', marginTop: 12 }}>
                Powered by Razorpay · Cart: <span style={{ fontFamily: "'DM Mono',monospace" }}>{cart_id}</span>
              </p>
            </div>
          )}

          {/* PROCESSING */}
          {phase === 'processing' && (
            <div style={{ textAlign: 'center', padding: '20px 0', animation: 'fadeUp 0.3s ease both' }}>
              <Loader label={step === 1 ? 'Opening payment window...' : step === 2 ? 'Verifying payment...' : 'Activating your tools...'} />
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 20 }}>Please do not close this window</p>
            </div>
          )}

          {/* SUCCESS */}
          {phase === 'success' && (
            <div style={{ textAlign: 'center', padding: '10px 0', animation: 'fadeUp 0.5s ease both' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#0369a1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 20px', boxShadow: '0 0 40px rgba(124,58,237,0.4)' }}>✓</div>
              <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Payment successful!</h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Your tools are now active and ready to use.</p>
              {txnId && <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', fontFamily: "'DM Mono',monospace", marginBottom: 28 }}>Payment ID: {txnId}</p>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/toolmanager')} style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#7c3aed,#0369a1)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Open my tools →</button>
                <button onClick={() => navigate('/dashboard')} style={{ padding: '12px 24px', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Dashboard</button>
              </div>
            </div>
          )}

          {/* FAILURE */}
          {phase === 'failure' && (
            <div style={{ textAlign: 'center', padding: '10px 0', animation: 'fadeUp 0.4s ease both' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '2px solid rgba(239,68,68,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 20px', color: '#f87171' }}>✕</div>
              <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Payment unsuccessful</h2>
              {error && <p style={{ fontSize: 13, color: '#f87171', marginBottom: 24, maxWidth: 360, margin: '0 auto 24px', lineHeight: 1.6 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => { setPhase('ready'); setStep(0); setError(''); }} disabled={!cartInfo} style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#7c3aed,#0369a1)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', opacity: cartInfo ? 1 : 0.5 }}>Try again</button>
                <button onClick={() => navigate('/services')} style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 14, cursor: 'pointer' }}>Back to plans</button>
              </div>
              <p style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>Need help? Email <a href="mailto:support@pilomation.com" style={{ color: '#7c3aed', textDecoration: 'none' }}>support@pilomation.com</a></p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}